import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Terminal,
  LogOut,
  ShieldCheck,
  User as UserIcon,
  FileText,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (p) =>
    p === "/" ? location.pathname === "/" : location.pathname.startsWith(p);

  const NavLink = ({ to, label, testid }) => (
    <Link
      to={to}
      data-testid={testid}
      className={cn(
        "mono text-[11px] uppercase tracking-[0.24em] px-3 py-1.5 border transition-colors",
        isActive(to)
          ? "border-[#E2FF54] text-[#E2FF54]"
          : "border-transparent text-neutral-500 hover:text-white hover:border-neutral-800"
      )}
    >
      {label}
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="grid-bg absolute inset-0 pointer-events-none opacity-40" />
      <header className="relative border-b border-neutral-900 px-4 md:px-12 py-4 md:py-5 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="brand-link">
          <div className="h-8 w-8 bg-[#E2FF54] flex items-center justify-center group-hover:brightness-110 transition">
            <Terminal size={16} className="text-black" strokeWidth={2.5} />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              utility
            </span>
            <span className="mono text-sm font-semibold text-white tracking-wider">
              FOP.REWRITE
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/" label="rewriter" testid="nav-rewriter" />
          {user && <NavLink to="/portal" label="portal" testid="nav-portal" />}
          {user?.role === "admin" && (
            <NavLink to="/admin" label="admin" testid="nav-admin" />
          )}
        </nav>

        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          {!user ? (
            <>
              <Link
                to="/login"
                data-testid="header-login-btn"
                className="mono text-[11px] uppercase tracking-[0.24em] text-neutral-400 hover:text-white px-3 py-1.5"
              >
                sign in
              </Link>
              <Link
                to="/signup"
                data-testid="header-signup-btn"
                className="btn-brutal mono text-[11px] uppercase tracking-[0.24em] bg-[#E2FF54] text-black hover:bg-[#d4f531] px-4 py-2"
              >
                sign up
              </Link>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center gap-1.5 md:gap-2 border border-neutral-800 hover:border-neutral-600 px-2 md:px-3 py-1.5 mono text-[11px] uppercase tracking-[0.2em] min-w-0 max-w-[200px] md:max-w-[280px]"
                data-testid="user-menu-trigger"
              >
                <UserIcon size={12} className="text-[#E2FF54] shrink-0" />
                <span className="hidden sm:inline max-w-[100px] md:max-w-[140px] truncate text-neutral-300">
                  {user.email}
                </span>
                <span className="sm:hidden text-neutral-300 max-w-[80px] truncate">
                  {user.email.split("@")[0]}
                </span>
                {user.role === "admin" && (
                  <span className="text-[9px] text-[#E2FF54] border border-[#E2FF54]/40 px-1 shrink-0">
                    admin
                  </span>
                )}
                <ChevronDown size={12} className="text-neutral-500 shrink-0" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="mono rounded-none bg-[#0a0a0a] border-neutral-800 text-neutral-300 min-w-[220px]"
              >
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.24em] text-neutral-500">
                  {user.name || "account"}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-neutral-900" />
                <DropdownMenuItem
                  onClick={() => navigate("/")}
                  className="rounded-none text-xs cursor-pointer focus:bg-neutral-900 focus:text-white"
                  data-testid="menu-rewriter"
                >
                  <FileText size={14} className="mr-2" /> rewriter
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/portal")}
                  className="rounded-none text-xs cursor-pointer focus:bg-neutral-900 focus:text-white"
                  data-testid="menu-portal"
                >
                  <UserIcon size={14} className="mr-2" /> my portal
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem
                    onClick={() => navigate("/admin")}
                    className="rounded-none text-xs cursor-pointer focus:bg-neutral-900 focus:text-white"
                    data-testid="menu-admin"
                  >
                    <ShieldCheck size={14} className="mr-2" /> admin panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-neutral-900" />
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    navigate("/login");
                  }}
                  className="rounded-none text-xs cursor-pointer text-[#FF9994] focus:bg-neutral-900 focus:text-[#FF9994]"
                  data-testid="menu-logout"
                >
                  <LogOut size={14} className="mr-2" /> log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </header>

      <main className="relative">{children}</main>
    </div>
  );
}
